import { Box, CircularProgress, Typography } from "@mui/joy";
import { useMemo } from "react";
import { Helmet } from "react-helmet";
import { CallMasthead } from "../atoms/callMasthead";
import { callTheme } from "../entities/callDesign";
import {
  PollView,
  pollViewFromFlatKeys,
} from "../entities/missionMap/missionMap";
import { Footer } from "../organisms/footer";
import { PollCard } from "../organisms/pollCard";
import { useGetMissionMapQuery } from "../redux/missionMap";
import { CallFooter } from "./modernInvite";

/**
 * PollInvite is the standalone musician-facing poll page (token prefix 'P'). It
 * reuses the public mission endpoints: the poll definition comes from
 * `missions/public/{token}` and the answer is a `pollAnswer` inspector posted
 * to `missions/mercuryPublic`. The poll itself renders via the shared PollCard.
 *
 * @returns {ReactElement} The poll answer page.
 */
export function PollInvite({ token }: { token: string }) {
  const { data, isLoading } = useGetMissionMapQuery(token ?? "", {
    skip: !token,
  });

  const poll = useMemo<PollView | undefined>(() => {
    if (!data) return undefined;
    // The standalone poll rides `Poll`; fall back to the first inline poll, then
    // to the flat lowercase keys, which is what a 'P' token actually returns.
    const source = data.Poll ?? data.Polls?.[0] ?? pollViewFromFlatKeys(data);
    if (!source) return undefined;
    return {
      ...source,
      publicToken: source.publicToken || data.PublicToken || token,
      greeting: source.greeting ?? data.Greetings,
    };
  }, [data, token]);

  // Modern Calls power-up: the poll wears the same paper as the call it came
  // with — white ground, org accent, masthead. Legacy orgs keep the full-bleed
  // company color. Server-gated: only opted-in orgs get ModernCall.
  const theme = callTheme(data);

  if (isLoading || !data) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flex: 1,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // The map loaded but carries no poll. Say so — spinning here reads as a page
  // that never loads, which is how a mapping the page can't parse showed up.
  if (!poll) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flex: 1,
          p: 4,
        }}
      >
        <Typography level="body-md" sx={{ textAlign: "center" }}>
          This poll is no longer available.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        background: theme.ground,
        alignItems: "center",
        position: "relative",
        flex: 1,
        minHeight: "100vh",
        fontFamily: theme.modern ? theme.fontStack : undefined,
      }}
    >
      <Helmet>
        <meta name="theme-color" content={theme.themeColor} />
        <title>{data.CompanyName}</title>
      </Helmet>
      <Box
        sx={{
          // Same column as the call: the modern call's 680px ceiling, the
          // legacy page's historical 480px.
          maxWidth: theme.modern ? 680 : 480,
          width: "100%",
          p: theme.modern ? 2 : 1,
          gap: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "start",
        }}
      >
        {theme.modern ? (
          <CallMasthead
            theme={theme}
            companyName={data.CompanyName}
            // The poll's own title is the page's heading; the masthead names
            // the KIND of call, as it does on an invite.
            kind="Poll"
            logoURL={data.CallMastheadLogo}
          />
        ) : data.CompanyLogo ? (
          <img src={data.CompanyLogo} style={{ height: 70, margin: "auto" }} />
        ) : (
          []
        )}
        <PollCard poll={poll} theme={theme} />
        {theme.modern ? <CallFooter companyName={data.CompanyName} /> : []}
      </Box>
      {theme.modern ? [] : <Footer token={token} />}
    </Box>
  );
}
